/*
 * SLEEPYHEAD — "You might also like" in the cart drawer.
 * File name: assets/sh-cart-recs.js (loaded by snippets/cart-drawer.liquid)
 *
 * - Suggests products related to the newest item in the cart, skipping
 *   anything already in the cart and anything sold out.
 * - "Add" puts the product in the cart and refreshes the drawer in place
 *   (free-shipping bar, items and total) without reloading the page.
 * - Refreshes itself whenever the cart changes.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const getJSON = (url) => fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json());

class ShCartRecs extends HTMLElement {
  #token = 0;
  #timer = 0;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    const after = event.promise || Promise.resolve();
    after
      .catch(() => {})
      .then(() => {
        clearTimeout(this.#timer);
        this.#timer = setTimeout(() => this.load(), 150);
      });
  };

  money(cents) {
    const n = cents / 100;
    const whole = Number.isInteger(n);
    const parts = n.toFixed(whole ? 0 : 2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = parts.join('.');
    const format = this.dataset.moneyFormat || 'R{{amount}}';
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  }

  async load() {
    const token = ++this.#token;
    try {
      const cart = await getJSON(ROOT + 'cart.js');
      if (token !== this.#token) return;
      if (!cart.items || !cart.items.length) return this.clear();

      const inCart = new Set(cart.items.map((i) => i.product_id));
      const seedId = cart.items[0].product_id;
      const data = await getJSON(
        ROOT + 'recommendations/products.json?product_id=' + seedId + '&limit=10&intent=related'
      );
      if (token !== this.#token) return;

      const picks = (data.products || [])
        .filter((p) => !inCart.has(p.id) && p.available)
        .slice(0, 4);

      if (!picks.length) return this.clear();
      this.render(picks);
    } catch (e) {
      if (token === this.#token) this.clear();
    }
  }

  clear() {
    this.innerHTML = '';
  }

  render(products) {
    const cards = products
      .map((p) => {
        const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
        if (!v) return '';
        const name = String(p.title).replace(/^sleepyhead™?\s*/i, '');
        const src = p.featured_image
          ? p.featured_image + (p.featured_image.includes('?') ? '&' : '?') + 'width=200'
          : '';
        const url = p.url || ROOT + 'products/' + p.handle;
        return (
          '<div class="sh-cartrecs__card">' +
          '<a class="sh-cartrecs__thumb" href="' + esc(url) + '" tabindex="-1" aria-hidden="true">' +
          (src ? '<img src="' + esc(src) + '" alt="" width="64" height="64" loading="lazy">' : '') +
          '</a>' +
          '<div class="sh-cartrecs__info">' +
          '<a class="sh-cartrecs__name" href="' + esc(url) + '">' + esc(name) + '</a>' +
          '<span class="sh-cartrecs__price">' + esc(this.money(v.price)) + '</span>' +
          '</div>' +
          '<button type="button" class="sh-cartrecs__add" data-variant-id="' + v.id + '" data-product-id="' + p.id + '" aria-label="Add ' + esc(name) + ' to cart">Add</button>' +
          '</div>'
        );
      })
      .join('');

    this.innerHTML =
      '<p class="sh-cartrecs__heading">You might also like</p>' +
      '<div class="sh-cartrecs__track">' + cards + '</div>';

    this.querySelectorAll('.sh-cartrecs__add').forEach((btn) => {
      btn.addEventListener('click', () => this.add(btn));
    });
  }

  async add(btn) {
    if (btn.disabled) return;
    const variantId = btn.dataset.variantId;
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.textContent = 'Adding';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map(
      (el) => el.dataset.sectionId
    );
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw data;

      const cart = await getJSON(ROOT + 'cart.js');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'sh-cart-recs',
          sourceId: 'sh-cart-recs',
          itemCount: 1,
          productId: btn.dataset.productId,
          sections: data.sections || {},
          didError: false,
        },
      });
    } catch (err) {
      deferred.reject(err);
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.textContent = 'Try again';
      setTimeout(() => {
        if (btn.isConnected) btn.textContent = 'Add';
      }, 2500);
    }
  }
}

if (!customElements.get('sh-cart-recs')) {
  customElements.define('sh-cart-recs', ShCartRecs);
}

/*
 * Buy box connection.
 * The Sleepyhead buy box announces "added to cart" with a 'cart:update' event
 * and opens the cart through <cart-drawer-component>.open(). This version of
 * the theme listens for a different event and opens the cart elsewhere, so
 * these two pieces translate between them: the drawer refreshes (items,
 * free-shipping bar, total, cart count) and slides open after every add.
 */
if (!window.__shBuyBoxBridge) {
  window.__shBuyBoxBridge = true;

  document.addEventListener('cart:update', (event) => {
    if (event instanceof CartLinesUpdateEvent) return;
    const detail = event.detail || {};
    const info = detail.data || {};
    const cart = detail.resource;
    if (!cart || !Array.isArray(cart.items) || info.source !== 'sh-product-buy') return;

    const deferred = CartLinesUpdateEvent.createPromise();
    const target = event.target instanceof Node ? event.target : document;
    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(info.variantId || ''), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'sh-product-buy',
        sourceId: detail.sourceId,
        itemCount: 1,
        productId: info.productId,
        sections: info.sections || {},
        didError: false,
      },
    });
  });

  customElements.whenDefined('cart-drawer-component').then(() => {
    const DrawerContent = customElements.get('cart-drawer-component');
    if (DrawerContent && !('open' in DrawerContent.prototype)) {
      DrawerContent.prototype.open = function () {
        const drawer = this.closest('theme-drawer');
        if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
      };
    }
  });
}
